import { ref, computed } from 'vue';
import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { auth } from '@ui-api3-sdk/api';

export const POINTS_TRANSFER_ENDPOINTS = {
  TO_WALLET: '/youzan/points/transfer/to-wallet',
  FROM_WALLET: '/youzan/points/transfer/from-wallet',
  BALANCE: '/youzan/points/balance',
  HEALTH_CHECK: '/youzan/app/health-check',
} as const;

export type PointsTransferPath =
  (typeof POINTS_TRANSFER_ENDPOINTS)[keyof typeof POINTS_TRANSFER_ENDPOINTS]

export type UUID = string

export interface PointsTransferPayload {
  accountId: number
  walletTypeId?: number
  amount?: number
}

export interface BalanceParams {
  accountId: number
}

export interface ApiErrorShape {
  code: number
  type: string
  description: string
  data: Record<string, unknown>
}

export interface ApiSuccess {
  success: true
  requestId: UUID
  payload?: Record<string, unknown>
}

export interface ApiFailure {
  success: false
  requestId: UUID
  error: ApiErrorShape
}

export type ApiResponse = ApiSuccess | ApiFailure

export interface UsePointsTransferOptions {
  client?: AxiosInstance
  baseURL?: string
  headers?: Record<string, string>
  debug?: boolean
}

function validatePayload(p: PointsTransferPayload) {
  if (!Number.isInteger(p.accountId) || p.accountId <= 0) {
    throw new Error('accountId должен быть положительным целым числом');
  }
  if (p.walletTypeId && (!Number.isInteger(p.walletTypeId) || p.walletTypeId <= 0)) {
    throw new Error('walletTypeId должен быть положительным целым числом');
  }
  if (p.amount && (!Number.isInteger(p.amount) || p.amount <= 0)) {
    throw new Error('amount должен быть положительным целым числом');
  }
}

function getAxiosErrorData(err: AxiosError): unknown {
  return err.response?.data;
}

export function usePointsTransfer(opts: UsePointsTransferOptions = {}) {
  const {
    client,
    baseURL = import.meta.env.VITE_APP_API_BASE_URL || '',
    headers = {
      'Content-Type': 'application/json',
      'authorization': `Bearer ${auth.getAccessToken()}`,
    },
    debug = false,
  } = opts;

  const axiosClient: AxiosInstance =
    client ??
    axios.create({
      baseURL,
      headers,
    });

  if (!client) {
    axiosClient.interceptors.response.use(
      (r) => {
        if (debug) {
          console.debug('[API OK]', r.config?.url);
        }
        return r;
      },
      (e) => {
        if (debug) {
          console.debug('[API ERR]', e.config?.url, e.response?.status, e.response?.data);
        }
        return Promise.reject(e);
      },
    );
  }

  const loading = ref(false);
  const lastRequestId = ref<UUID | null>(null);
  const lastError = ref<ApiErrorShape | null>(null);
  const hasError = computed(() => lastError.value !== null);

  async function call(
    path: PointsTransferPath,
    method: 'post' | 'get',
    payload?: PointsTransferPayload,
  ): Promise<ApiSuccess> {
    if (payload) validatePayload(payload);

    loading.value = true;
    lastError.value = null;
    lastRequestId.value = null;

    try {
      const { data } = await axiosClient.request<ApiResponse>({
        url: path,
        method,
        ...(method === 'get'
          ? { params: payload }
          : { data: payload }),
      });

      lastRequestId.value = data.requestId;

      if (data.success === false) {
        lastError.value = data.error;
        throw new Error(
          `[${data.error.code}] ${data.error.type}${data.error.description ? ` — ${data.error.description}` : ''}`,
        );
      }

      return data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const body = getAxiosErrorData(err) as Partial<ApiFailure> | undefined;
        if (body && typeof body === 'object' && 'success' in body && body.success === false && body.error) {
          lastRequestId.value = (body.requestId ?? null) as UUID | null;
          lastError.value = body.error as ApiErrorShape;
        }
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function transferToWallet(payload: PointsTransferPayload) {
    return call(POINTS_TRANSFER_ENDPOINTS.TO_WALLET, 'post', payload);
  }


  function transferFromWallet(payload: PointsTransferPayload) {
    return call(POINTS_TRANSFER_ENDPOINTS.FROM_WALLET, 'post', payload);
  }

  function getBalance(accountId: number) {
    return call(POINTS_TRANSFER_ENDPOINTS.BALANCE, 'get', { accountId });
  }

  function healthCheck() {
    return call(POINTS_TRANSFER_ENDPOINTS.HEALTH_CHECK, 'get');
  }

  function resetState() {
    loading.value = false;
    lastRequestId.value = null;
    lastError.value = null;
  }

  return {
    loading,
    lastRequestId,
    lastError,
    hasError,
    transferToWallet,
    transferFromWallet,
    getBalance,
    healthCheck,
  };
}
